import { RouteGroup } from '@/modules/core/app/constants/api.constants';
import { BaseResource } from '@/modules/core/app/resources/BaseResource';
import { DataView } from '@/modules/core/datasource/models/DataView';
import { GenericDataModel } from '@/modules/core/app/models/GenericDataModel';
import { GenericModel } from '@/modules/core/app/models/GenericModel';
import ApiColumn from '@/modules/core/app/models/api/ApiColumn';
import { isEmpty, isPlainObject } from 'lodash';

export class ServiceDataResource extends BaseResource {
  constructor() {
    super(RouteGroup.SERVICES);
  }

  /**
   * @param {string} serviceId
   * @param params
   * @returns {Promise<DataView[]>}
   * */
  async getDataViews(serviceId, params = {}) {
    return super.getData(DataView, `${serviceId}/data/data_views`, params);
  }

  /**
   * @param serviceId
   * @param dataViewId
   * @param params
   * @param widget
   * @returns {Promise<GenericDataModel[]>}
   * */
  async getData(serviceId, dataViewId, params = {}, widget = {}, config = {}) {
    let data;
    if (Array.isArray(params.fields)) {
      const fieldsQueryParam = params.fields
        // eslint-disable-next-line tap/no-raw-text-js
        .map((f) => `fields=${encodeURIComponent(f)}`)
        .join('&');
      delete params.fields;
      data = await this.apiHelper.get(
        this._buildRoute(`${serviceId}/data/${dataViewId}?${fieldsQueryParam}`),
        {
          params,
          ...config,
        }
      );
    } else {
      data = await this.apiHelper.get(this._buildRoute(`${serviceId}/data/${dataViewId}`), {
        params,
        ...config,
      });
    }

    const isWidgetRequest = params?.widget_request;
    /* eslint-disable tap/no-raw-text-js */
    const isStructured =
      isPlainObject(data) &&
      'currency_discrepancy' in data &&
      'show_currency' in data &&
      'data' in data;
    /* eslint-disable tap/no-raw-text-js */

    if (isWidgetRequest) {
      if (!isEmpty(widget?.metadata)) {
        widget.metadata.currency_discrepancy = false;
        widget.metadata.show_currency = false;
      }
      if (isStructured) {
        const { currency_discrepancy, show_currency, data: mainData, total_data } = data;

        if (!isEmpty(widget) && widget.metadata) {
          widget.metadata.currency_discrepancy = currency_discrepancy;
          widget.metadata.show_currency = show_currency;

          const payload = total_data ? { data: mainData, total_data } : mainData;
          return this.extractData(payload, GenericDataModel, params);
        }

        return this.extractData(mainData, GenericDataModel, params);
      }
    }

    return this.extractData(data, GenericDataModel, params);
  }

  /**
   *
   * @param data_source_id
   * @param data_view_id
   * @param params
   * @returns {Promise<ApiColumn>}
   */
  async getColumns(data_source_id, data_view_id, params = {}) {
    params = { ...params, metadata: true };
    return super.getData(ApiColumn, `${data_source_id}/data/${data_view_id}`, params);
  }

  /**
   *
   * @param serviceId
   * @param dataViewId
   * @param fields
   * @param params
   * @returns {Promise<*>}
   */
  async getValuesDistinct(serviceId, dataViewId, fields, params = {}) {
    params = { ...params, fields };
    return super.getData(GenericModel, `${serviceId}/data/${dataViewId}/values/distinct`, params);
  }
}

export default new ServiceDataResource();
